import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import classNames from 'classnames';
import HelmetWrapper from 'components/HelmetWrapper';
import { useTranslation } from 'react-i18next';
import dataRoomApi from 'router/subrouters/DataRoom/dataRoomApi';
import { DataRoomShareableLink } from 'models/ShareableLink';
import useQueryParams from 'hooks/useQueryParams';
import usePageParams from 'hooks/usePageParams';
import { getDataRoomRoute, sentryError } from 'utils';
import ShareableLinksEmpty from './components/ShareableLinksEmpty';
import ShareableLinksTable from './components/ShareableLinksTable';
import DataRoomContext from 'router/subrouters/DataRoom/providers/DataRoom/DataRoom.context';
import { Loader } from 'ncoded-component-library';
import ShareableLinksMobile from './components/ShareableLinksMobile';
import useMatchMedia from 'hooks/useMatchMedia';
import { useNavigate } from 'react-router-dom';
import EditShareableLinkProvider from 'router/subrouters/DataRoom/providers/EditShareableLink';
import CreateShareableLinkProvider from 'router/subrouters/DataRoom/providers/CreateShareableLink';
import useRefetch from 'hooks/useRefetch';
import { MOBILE_SECURE_LINKS_SELECTOR } from 'router/subrouters/DataRoom/providers/Onboarding/data';

import './ShareableLinks.styles.scss';

const LIMIT = '10';
const DEFAULT_SORT = '-createdAt';

type ShareableLinksProps = {
  className?: string;
};

const ShareableLinks: React.FC<ShareableLinksProps> = (props) => {
  const { className } = props;

  const classes = classNames('shareable-links', 'page', className);

  const [isLoading, setIsLoading] = useState(true);
  const [totalPages, setTotalPages] = useState(0);
  const [shareableLinks, setShareableLinks] = useState<DataRoomShareableLink[]>(
    [],
  );

  const { refetch, refetchFlag } = useRefetch();

  const {
    dataRoom: { _id: dataRoomId, name },
  } = useContext(DataRoomContext);

  const { t } = useTranslation();
  const { params } = useQueryParams();

  const navigate = useNavigate();

  const isPhablet = useMatchMedia('isPhablet');

  const page = usePageParams();
  const { sort = DEFAULT_SORT } = params;

  const isEmpty = useMemo(
    () => !shareableLinks.length,
    [shareableLinks.length],
  );

  const updateShareableLink = useCallback(
    (shareableLink: DataRoomShareableLink) =>
      setShareableLinks((old) =>
        old.map((link) =>
          link?._id === shareableLink?._id ? shareableLink : link,
        ),
      ),
    [],
  );

  const onRowClick = useCallback(
    ({ _id }: DataRoomShareableLink) =>
      navigate(getDataRoomRoute(dataRoomId, 'shareable-links') + `/${_id}`),
    [dataRoomId, navigate],
  );

  useEffect(() => {
    (async () => {
      setIsLoading(true);
      try {
        const {
          data: { items, totalPages },
        } = await dataRoomApi.shareableLinks.getShareableLinks(dataRoomId, {
          $page: page,
          $limit: LIMIT,
          $sort: sort,
          $relations: '["dataRoomNodes", "dataRoomNda"]',
        });

        setShareableLinks(items);
        setTotalPages(totalPages);
      } catch (error) {
        sentryError(error);
      } finally {
        setIsLoading(false);
      }
    })();
  }, [dataRoomId, page, sort, refetchFlag]);

  return (
    <div className={classes}>
      <HelmetWrapper title={`${t('secureShares')} - ${name}`} />
      <h2 id={MOBILE_SECURE_LINKS_SELECTOR}>{t('secureShares')}</h2>

      <CreateShareableLinkProvider onSuccessFullyCreated={() => refetch()}>
        <EditShareableLinkProvider updateShareableLink={updateShareableLink}>
          {!isLoading && isEmpty ? (
            <ShareableLinksEmpty />
          ) : isPhablet ? (
            <ShareableLinksMobile
              isLoading={isLoading}
              totalPages={totalPages}
              shareableLinks={shareableLinks}
              updateShareableLink={updateShareableLink}
              onRowClick={onRowClick}
            />
          ) : (
            <ShareableLinksTable
              isLoading={isLoading}
              totalPages={totalPages}
              shareableLinks={shareableLinks}
              updateShareableLink={updateShareableLink}
              onRowClick={onRowClick}
            />
          )}
        </EditShareableLinkProvider>
      </CreateShareableLinkProvider>

      {isLoading && !isEmpty && <Loader />}
    </div>
  );
};

export default ShareableLinks;
